'use client';

import { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '../../toast/ToastProvider';
import { MAX_APPLICATION_DOCS_FILES } from './applicationDocumentation.constants';
import type {
  ApplicationDocumentationController,
  DocumentationFileCategory,
} from './applicationDocumentation.types';
import { mergeDocumentationDialogFiles } from './applicationDocumentationFiles.utils';
import type { InitialDocumentationActionResolver } from './useInitialDocumentationAction';
import type { StagedDocumentationState } from './useStagedDocumentation';

export type DocumentationFileDialogState = {
  category: DocumentationFileCategory | null;
  files: File[];
  fileUploadKey: number;
  uploadingCategory: DocumentationFileCategory | null;
  isConfirming: boolean;
  open: (category: DocumentationFileCategory) => void;
  dismiss: () => void;
  confirm: () => Promise<void>;
  acceptFiles: (details: { files: File[] }) => void;
  removeFile: ({ fileIndex }: { fileIndex: number }) => void;
};

export const useDocumentationFileDialog = ({
  applicationId,
  isDeferred,
  showsSubmittingState,
  uploadFiles,
  stageFiles,
  initialAction,
}: {
  applicationId: string | null;
  isDeferred: boolean;
  showsSubmittingState: boolean;
  uploadFiles: ApplicationDocumentationController['uploadFiles'];
  stageFiles: StagedDocumentationState['stageFiles'];
  initialAction: InitialDocumentationActionResolver;
}): DocumentationFileDialogState => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const [category, setCategory] = useState<DocumentationFileCategory | null>(
    null,
  );
  const [files, setFiles] = useState<File[]>([]);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [uploadingCategory, setUploadingCategory] =
    useState<DocumentationFileCategory | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const filesRef = useRef<File[]>([]);
  const { resolveAdvance, resolveCancel, beginAdvance, endAdvance } =
    initialAction;

  const replaceFiles = useCallback((nextFiles: File[]) => {
    filesRef.current = nextFiles;
    setFiles(nextFiles);
  }, []);

  const dismiss = useCallback(() => {
    setCategory(null);
    replaceFiles([]);
    setFileUploadKey((key) => key + 1);
    resolveCancel();
    endAdvance();
  }, [endAdvance, replaceFiles, resolveCancel]);

  const confirm = useCallback(async () => {
    if (!applicationId || !category || files.length === 0) {
      return;
    }

    const confirmedCategory = category;
    const filesToUpload = [...files];

    if (showsSubmittingState) {
      setIsConfirming(true);
    }

    try {
      if (isDeferred) {
        stageFiles({ category: confirmedCategory, files: filesToUpload });
        beginAdvance();
        dismiss();
        resolveAdvance();

        return;
      }

      setUploadingCategory(confirmedCategory);
      beginAdvance();
      dismiss();
      await uploadFiles({
        applicationId,
        category: confirmedCategory,
        files: filesToUpload,
      });
      toasterApi.success({ description: t('filesAddedSuccessfully') });
      resolveAdvance();
    } catch (error) {
      console.error(
        new Error('Failed to upload documentation files', {
          cause: error instanceof Error ? error : new Error(String(error)),
        }),
      );
      toasterApi.error({
        title: t('errors.failedToUploadFiles'),
        description: t('errors.pleaseTryAgain'),
      });
    } finally {
      setUploadingCategory(null);
      if (showsSubmittingState) {
        setIsConfirming(false);
      }
    }
  }, [
    applicationId,
    beginAdvance,
    category,
    dismiss,
    files,
    isDeferred,
    resolveAdvance,
    showsSubmittingState,
    stageFiles,
    t,
    uploadFiles,
  ]);

  const open = useCallback(
    (nextCategory: DocumentationFileCategory) => {
      if (!applicationId || uploadingCategory !== null) {
        return;
      }

      replaceFiles([]);
      setFileUploadKey((key) => key + 1);
      setCategory(nextCategory);
    },
    [applicationId, replaceFiles, uploadingCategory],
  );

  const acceptFiles = useCallback(
    (details: { files: File[] }) => {
      const merged = mergeDocumentationDialogFiles({
        previousFiles: filesRef.current,
        newFiles: details.files ?? [],
      });

      replaceFiles(merged.files);
      if (merged.hasTooManyFiles) {
        toasterApi.error({
          title: t('errors.failedToUploadFiles'),
          description: t('errors.tooManyFiles', {
            count: MAX_APPLICATION_DOCS_FILES,
          }),
        });
      }
    },
    [replaceFiles, t],
  );

  const removeFile = useCallback(
    ({ fileIndex }: { fileIndex: number }) => {
      replaceFiles(filesRef.current.filter((_, index) => index !== fileIndex));
      setFileUploadKey((key) => key + 1);
    },
    [replaceFiles],
  );

  return {
    category,
    files,
    fileUploadKey,
    uploadingCategory,
    isConfirming,
    open,
    dismiss,
    confirm,
    acceptFiles,
    removeFile,
  };
};
